import { useMemo } from 'react';
import styled from 'styled-components';

import { createColumnHelper } from '@tanstack/react-table';

import DataTable from 'components/shared/DataTable';
import Menu from 'components/shared/menu';
import { simpleDate } from 'lib/date';
import { MappingEntry, MappingType, ReceivedValue } from 'services/api/admin/mappings';
import { colors } from 'styles/theme/colors';
import CheckIcon from 'svg/CheckIcon';
import EyeOffOutlineIcon from 'svg/EyeOffOutlineIcon';
import PencilIconOutline from 'svg/PencilIconOutline';
import ThreeDotMenu from 'svg/ThreeDotMenu';
import TrashIcon from 'svg/TrashIcon';

import { Chip, LinkButton, Mono, Muted, Struck } from './Chips';
import OperationProblem from './OperationProblem';
import { PendingOperation, percent, slotOf, targetText } from './operations';

export type MappingRow = {
  key: string;
  entry: MappingEntry | null;
  pending: PendingOperation | null;
  value: ReceivedValue | null;
};

type Props = {
  type: MappingType;
  rows: MappingRow[];
  recordNoun: string;
  disabled: boolean;
  onChange: (row: MappingRow) => void;
  onDontMap: (row: MappingRow) => void;
  onAcknowledge: (row: MappingRow) => void;
  onRemove: (row: MappingRow) => void;
  onUndo: (slot: string) => void;
  onKeepMine: (op: PendingOperation) => void;
};

const columnHelper = createColumnHelper<MappingRow>();

function currentLabel(entry: MappingEntry | null) {
  if (!entry) return null;
  if (entry.dontMap) return <Chip>Don&apos;t map</Chip>;
  if (entry.resolved) return <span>{entry.resolved.name}</span>;
  return <Chip $tone='red'>Not found: {targetText(entry.target)}</Chip>;
}

function pendingLabel(op: PendingOperation) {
  switch (op.op) {
    case 'set':
      return op.label ?? targetText(op.target);
    case 'dont_map':
      return "Don't map";
    case 'remove':
      return 'Unmapped';
    case 'acknowledge':
      return 'Acknowledged';
    case 'accept_suggestion':
      return op.label ?? 'Agent suggestion';
    default:
      return null;
  }
}

export default function MappingsTable({
  type,
  rows,
  recordNoun,
  disabled,
  onChange,
  onDontMap,
  onAcknowledge,
  onRemove,
  onUndo,
  onKeepMine,
}: Props) {
  const canDontMap = type !== 'group_types';

  const columns = useMemo(
    () => [
      columnHelper.accessor('key', {
        header: 'Received value',
        cell: (info) => <Mono>{info.getValue()}</Mono>,
      }),
      columnHelper.display({
        id: 'target',
        header: 'Maps to',
        cell: ({ row: { original: row } }) => {
          const { entry, pending } = row;
          if (!pending || pending.op === 'acknowledge') {
            return (
              <Cell>
                {currentLabel(entry)}
                {entry?.needsReview && !pending && <Chip $tone='red'>Needs review</Chip>}
                {pending && <Chip $tone='amber'>Pending acknowledge</Chip>}
                <OperationProblem op={pending} onKeepMine={onKeepMine} onUndo={onUndo} />
              </Cell>
            );
          }
          return (
            <Cell>
              {entry && (
                <Struck>{entry.dontMap ? "Don't map" : (entry.resolved?.name ?? targetText(entry.target))}</Struck>
              )}
              {entry && '→'}
              <span>{pendingLabel(pending)}</span>
              <Chip $tone='amber'>Pending</Chip>
              <OperationProblem op={pending} onKeepMine={onKeepMine} onUndo={onUndo} />
            </Cell>
          );
        },
      }),
      columnHelper.display({
        id: 'setBy',
        header: 'Set by',
        cell: ({ row: { original: row } }) => {
          const entry = row.entry;
          if (!entry?.setBy) return <Muted>—</Muted>;
          const who =
            entry.setBy === 'agent'
              ? ['Agent', percent(entry.confidence ?? entry.agentConfidence)].filter(Boolean).join(' · ')
              : (entry.actorLabel ?? 'Person');
          return <Muted>{[who, entry.setAt ? simpleDate(entry.setAt) : null].filter(Boolean).join(' · ')}</Muted>;
        },
      }),
      columnHelper.display({
        id: 'records',
        header: 'Records',
        cell: ({ row: { original: row } }) =>
          row.value ? (
            <Muted>
              {row.value.recordCount} {recordNoun}
              {row.value.recordCount === 1 ? '' : 's'}
              {row.value.lastSeenAt ? ` · last ${simpleDate(row.value.lastSeenAt)}` : ''}
            </Muted>
          ) : (
            <Muted>—</Muted>
          ),
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        cell: ({ row: { original: row } }) => {
          if (row.pending) return <LinkButton onClick={() => onUndo(slotOf(row.pending!))}>Undo</LinkButton>;
          if (disabled) return null;
          const showDontMap = canDontMap && !row.entry?.dontMap;
          return (
            <Actions>
              <Menu $size='small'>
                <Menu.Trigger>
                  <KebabButton type='button' aria-label='Row actions'>
                    <ThreeDotMenu />
                  </KebabButton>
                </Menu.Trigger>
                <Menu.Content position='right'>
                  {row.entry?.needsReview && (
                    <Menu.Item onClick={() => onAcknowledge(row)}>
                      <CheckIcon width={14} height={14} fill='currentColor' />
                      Acknowledge
                    </Menu.Item>
                  )}
                  <Menu.Item onClick={() => onChange(row)}>
                    <PencilIconOutline width={14} height={14} />
                    Change
                  </Menu.Item>
                  {showDontMap && (
                    <Menu.Item onClick={() => onDontMap(row)}>
                      <EyeOffOutlineIcon width={14} height={14} />
                      Don&apos;t map
                    </Menu.Item>
                  )}
                  {row.entry && <Menu.Divider />}
                  {row.entry && (
                    <RemoveItem onClick={() => onRemove(row)}>
                      <TrashIcon width={14} height={14} color={colors.red100} />
                      Remove
                    </RemoveItem>
                  )}
                </Menu.Content>
              </Menu>
            </Actions>
          );
        },
      }),
    ],
    [disabled, canDontMap, recordNoun, onChange, onDontMap, onAcknowledge, onRemove, onUndo, onKeepMine]
  );

  return <DataTable columns={columns} data={rows} defaultSortBy={[{ id: 'key', desc: false }]} />;
}

const Cell = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const KebabButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
`;

const RemoveItem = styled(Menu.Item)`
  color: ${({ theme }) => theme.colors.red100};
`;
